import "./ai-consent-shared.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Col, Hr, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import IconAlertCircleFillSmall from "@amzn/stencil-react-icons/icons/icon-alert-circle-fill-small";
import { useState } from "react";
import { FormattedMessage } from "react-intl";

import { AIWorkflowMetricsHelper } from "../../helpers/workflow-metrics-helper";
import { markAiAutomationAttempted } from "./constants";

interface AIOptInDisclosureProps {
  /** Content to display (title, description, sections, etc.) */
  readonly children: React.ReactNode;
  /** Agent type for metrics tracking (currently only "vpn" is supported) */
  readonly agentType: "vpn";
  /** Translation ID for auto VPN setup consent checkbox text */
  readonly autoVpnSetupConsentTextI18nId: string;
  /** Translation ID for screenshot collection consent checkbox text */
  readonly screenshotCollectionConsentTextI18nId: string;
  /** Translation ID for decline button */
  readonly declineButtonTextI18nId: string;
  /** Translation ID for start button */
  readonly startButtonTextI18nId: string;
  /** Callback when user starts automation */
  readonly onStartAutomation: () => void;
  /** Callback when user declines automation */
  readonly onDeclineAutomation: () => void;
  /**
   * Whether consent was already given for the whole session. The checkboxes are then omitted and
   * Start needs no ticking, because asking again would imply the earlier answer did not count.
   */
  readonly isConsentAlreadyGranted?: boolean;
  /** Initial state for auto VPN setup consent checkbox */
  readonly initialAutoVpnSetupConsent?: boolean;
  /** Initial state for screenshot collection consent checkbox */
  readonly initialScreenshotCollectionConsent?: boolean;
  readonly dataTestId?: string;
}

type AgentType = AIOptInDisclosureProps["agentType"];
type ConsentKey = "autoVpnSetup" | "screenshotCollection";

const STORAGE_KEY_PREFIX = "aiOptInDisclosure" as const;

const getStoredConsent = (
  agentType: AgentType,
  key: ConsentKey
): boolean | null => {
  try {
    const val: string | null = localStorage.getItem(
      `${STORAGE_KEY_PREFIX}.${agentType}.${key}`
    );
    return val === null ? null : val === "true";
  } catch {
    return null;
  }
};

const setStoredConsent = (
  agentType: AgentType,
  key: ConsentKey,
  value: boolean
): void => {
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}.${agentType}.${key}`,
      String(value)
    );
  } catch {
    // localStorage unavailable
  }
};

/**
 * Component that presents the AI automation opt-in disclosure to users,
 * explaining how AI will automate the VPN connection process.
 */
export function AIOptInDisclosure({
  children,
  agentType,
  autoVpnSetupConsentTextI18nId,
  screenshotCollectionConsentTextI18nId,
  declineButtonTextI18nId,
  startButtonTextI18nId,
  onStartAutomation,
  onDeclineAutomation,
  isConsentAlreadyGranted = false,
  initialAutoVpnSetupConsent = false,
  initialScreenshotCollectionConsent = false,
  dataTestId = "aiOptInDisclosure",
}: AIOptInDisclosureProps): JSX.Element {
  const [autoVpnSetupConsentChecked, setAutoVpnSetupConsentChecked] =
    useState<boolean>(
      getStoredConsent(agentType, "autoVpnSetup") ?? initialAutoVpnSetupConsent
    );
  const [
    screenshotCollectionConsentChecked,
    setScreenshotCollectionConsentChecked,
  ] = useState<boolean>(
    getStoredConsent(agentType, "screenshotCollection") ??
      initialScreenshotCollectionConsent
  );
  const [showError, setShowError] = useState<boolean>(false);

  const handleStartClick = () => {
    if (
      isConsentAlreadyGranted ||
      (autoVpnSetupConsentChecked && screenshotCollectionConsentChecked)
    ) {
      setShowError(false);
      AIWorkflowMetricsHelper.emitAIOptIn(agentType);
      markAiAutomationAttempted();
      onStartAutomation();
    } else {
      setShowError(true);
    }
  };

  const handleDeclineClick = () => {
    AIWorkflowMetricsHelper.emitAIOptOut(agentType);
    onDeclineAutomation();
  };

  const handleAutoVpnSetupConsentChange = (checked: boolean) => {
    setAutoVpnSetupConsentChecked(checked);
    setStoredConsent(agentType, "autoVpnSetup", checked);

    if (checked) {
      AIWorkflowMetricsHelper.emitAIControlConsentChecked(agentType);
    } else {
      AIWorkflowMetricsHelper.emitAIControlConsentUnchecked(agentType);
    }

    // Clear error when both checkboxes are checked
    if (showError && checked && screenshotCollectionConsentChecked) {
      setShowError(false);
    }
  };

  const handleScreenshotCollectionConsentChange = (checked: boolean) => {
    setScreenshotCollectionConsentChecked(checked);
    setStoredConsent(agentType, "screenshotCollection", checked);

    if (checked) {
      AIWorkflowMetricsHelper.emitScreenshotDataStorageConsentChecked(
        agentType
      );
    } else {
      AIWorkflowMetricsHelper.emitScreenshotDataStorageConsentUnchecked(
        agentType
      );
    }

    // Clear error when both checkboxes are checked
    if (showError && autoVpnSetupConsentChecked && checked) {
      setShowError(false);
    }
  };

  return (
    <Col gridGap="S400" dataTestId={dataTestId}>
      {children}

      <Hr color="neutral20" size="narrow" />
      {!isConsentAlreadyGranted && (
        <Col gridGap="S200">
          <Row gridGap="S300" alignItems="flex-start">
            <Checkbox
              id="ai-auto-vpn-setup-consent"
              name="ai-auto-vpn-setup-consent"
              checked={autoVpnSetupConsentChecked}
              onChange={(e) =>
                handleAutoVpnSetupConsentChange(e.target.checked)
              }
              error={showError && !autoVpnSetupConsentChecked}
              dataTestId="aiConsent1Checkbox"
            />
            <Text fontSize="T200" fontWeight="regular">
              <FormattedMessage id={autoVpnSetupConsentTextI18nId} />
            </Text>
          </Row>

          <Row gridGap="S300" alignItems="flex-start">
            <Checkbox
              id="ai-screenshot-collection-consent"
              name="ai-screenshot-collection-consent"
              checked={screenshotCollectionConsentChecked}
              onChange={(e) =>
                handleScreenshotCollectionConsentChange(e.target.checked)
              }
              error={showError && !screenshotCollectionConsentChecked}
              dataTestId="aiConsent2Checkbox"
            />
            <Text fontSize="T200" fontWeight="regular">
              <FormattedMessage id={screenshotCollectionConsentTextI18nId} />
            </Text>
          </Row>
        </Col>
      )}

      {showError && (
        <Row gridGap="S200" alignItems="flex-start">
          <IconAlertCircleFillSmall
            color="color.status.critical-icon"
            aria-hidden={true}
          />
          <Text
            fontSize="T100"
            color="color.text.secondary"
            fontWeight="regular"
          >
            <FormattedMessage id="connectVpn-ai-disclosure-error-required" />
          </Text>
        </Row>
      )}

      <Row gridGap="S300" className="ai-disclosure-buttons">
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleStartClick}
          dataTestId="aiStartButton"
        >
          <FormattedMessage id={startButtonTextI18nId} />
        </Button>
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={handleDeclineClick}
          dataTestId="aiDeclineButton"
        >
          <FormattedMessage id={declineButtonTextI18nId} />
        </Button>
      </Row>
    </Col>
  );
}
